import { useState, useEffect, useRef } from "react";
import { Volume2, Pause, Play, VolumeX } from "lucide-react";
import useSpeechSynthesis from "../hooks/useSpeechSynthesis";
import { getMessageActionClassName, MESSAGE_ACTION_STYLES } from "../utils/messageActionStyles";

const SpeechSynthesis = ({ displayText, onSelect, messageId }) => {
  const { speak, pause, resume, stop, isSpeaking, isPaused } =
    useSpeechSynthesis(onSelect);
  const [isToggling, setIsToggling] = useState(false);
  const previousTextRef = useRef(displayText);

  useEffect(() => {
    if (previousTextRef.current !== displayText && isSpeaking) {
      stop();
      requestAnimationFrame(() => speak(displayText));
    }
    previousTextRef.current = displayText;
  }, [displayText, isSpeaking, stop, speak]);

  const handlePlayPauseToggle = () => {
    if (isToggling) return;

    setIsToggling(true);
    onSelect(messageId);
    if (isSpeaking) {
      if (isPaused) {
        resume();
      } else {
        pause();
      }
    } else {
      speak(displayText);
    }
    setTimeout(() => setIsToggling(false), 300);
  };

  const handleStop = (e) => {
    e.stopPropagation();
    if (isToggling) return;

    setIsToggling(true);
    stop();
    onSelect(null);
    setTimeout(() => setIsToggling(false), 300);
  };

  return (
    <>
      <div className="flex justify-start mb-2 items-center gap-2">
        {isSpeaking && (
          <button
            onClick={handleStop}
            className={getMessageActionClassName('speaker', false, isToggling)}
            title="Stop"
            disabled={isToggling}
            aria-label="Stop speaking"
          >
            <VolumeX size={MESSAGE_ACTION_STYLES.ICON_SIZE} className={MESSAGE_ACTION_STYLES.COLORS.SPEAKER.DEFAULT} />
          </button>
        )}

        <button
          onClick={handlePlayPauseToggle}
          className={getMessageActionClassName('speaker', isSpeaking || isPaused, isToggling)}
          title={isPaused ? "Resume" : isSpeaking ? "Pause" : "Read Aloud"}
          disabled={isToggling}
          aria-label={
            isPaused
              ? "Resume speaking"
              : isSpeaking
              ? "Pause speaking"
              : "Read aloud"
          }
        >
          {isPaused ? (
            <Play size={MESSAGE_ACTION_STYLES.ICON_SIZE} className={isSpeaking || isPaused ? MESSAGE_ACTION_STYLES.COLORS.SPEAKER.ACTIVE : MESSAGE_ACTION_STYLES.COLORS.SPEAKER.DEFAULT} />
          ) : isSpeaking ? (
            <Pause size={MESSAGE_ACTION_STYLES.ICON_SIZE} className={MESSAGE_ACTION_STYLES.COLORS.SPEAKER.ACTIVE} />
          ) : (
            <Volume2 size={MESSAGE_ACTION_STYLES.ICON_SIZE} className={MESSAGE_ACTION_STYLES.COLORS.SPEAKER.DEFAULT} />
          )}
        </button>
      </div>
    </>
  );
};

export default SpeechSynthesis;
